import React, { useRef, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import getFullUrl from '../../utils/formatUrl';

const LogoMarquee = ({ logos = [], title = "Affiliation &", highlight = "Accreditation", description = "Our excellence is validated by major industrial and academic regulatory bodies across India." }) => {
    const scrollRef = useRef(null);
    const [isPaused, setIsPaused] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    const scroll = (direction) => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({
                left: direction,
                behavior: 'smooth'
            });
        }
    };
    

    useEffect(() => {
        const scrollContainer = scrollRef.current;
        if (!scrollContainer) return;

        let animationFrameId;
        const scrollAmount = 1; // Speed of auto-scroll
        
        const autoScroll = () => {
            if (!isPaused && !isDragging) {
                scrollContainer.scrollLeft += scrollAmount;
                
                // Seamless wrap-around logic
                if (scrollContainer.scrollLeft >= scrollContainer.scrollWidth / 2) {
                    scrollContainer.scrollLeft = 0;
                }
            }
            animationFrameId = requestAnimationFrame(autoScroll);
        };

        animationFrameId = requestAnimationFrame(autoScroll);
        return () => cancelAnimationFrame(animationFrameId);
    }, [isPaused, isDragging]);

    // ── Mouse handlers ──
    const handleMouseDown = (e) => {
        setIsDragging(true);
        setIsPaused(true);
        setStartX(e.pageX - scrollRef.current.offsetLeft);
        setScrollLeft(scrollRef.current.scrollLeft);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
        setIsPaused(false);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        setTimeout(() => setIsPaused(false), 500);
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 2;
        scrollRef.current.scrollLeft = scrollLeft - walk;
    };

    // ── Touch handlers ──
    const handleTouchStart = (e) => {
        setIsDragging(true);
        setIsPaused(true);
        setStartX(e.touches[0].pageX - scrollRef.current.offsetLeft);
        setScrollLeft(scrollRef.current.scrollLeft);
    };

    const handleTouchMove = (e) => {
        if (!isDragging) return;
        const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 1.5;
        scrollRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
        setTimeout(() => setIsPaused(false), 800);
    };

    if (!logos || logos.length === 0) return null;

    // Duplicate logos for seamless scroll
    const extendedLogos = [...logos, ...logos, ...logos, ...logos];

    return (
        <section className="py-10 bg-white border-y border-slate-50 overflow-hidden relative select-none">
            
            <div className="max-w-7xl mx-auto px-6 mb-8">
                <div className="space-y-6">
                    <div className="space-y-4">
                        <div className="h-1 w-16 bg-[#cc0000]"></div>
                        <h2 className="text-4xl md:text-5xl font-black text-[#003366] uppercase tracking-tighter leading-none">
                            {title} <span className="text-[#cc0000]">{highlight}</span>
                        </h2>
                    </div>
                    {description && (
                        <p className="max-w-2xl text-slate-500 font-medium text-sm md:text-base leading-relaxed text-left">
                            {description}
                        </p>
                    )}
                </div>
            </div>

            <div className="w-full relative py-4 md:py-6 bg-slate-50/50 group">
                {/* Visual Vignetting */}
                <div className="absolute left-0 top-0 bottom-0 w-10 md:w-80 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none" />
                
                <div 
                    ref={scrollRef}
                    className={`flex gap-3 md:gap-16 px-4 md:px-10 items-center overflow-x-hidden whitespace-nowrap cursor-${isDragging ? 'grabbing' : 'grab'}`}
                    style={{ touchAction: 'pan-x' }}
                    onMouseDown={handleMouseDown}
                    onMouseLeave={handleMouseLeave}
                    onMouseUp={handleMouseUp}
                    onMouseMove={handleMouseMove}
                    onMouseEnter={() => setIsPaused(true)}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    {extendedLogos.map((item, idx) => {
                        const logoUrl = typeof item === 'string' ? item : item.logo;
                        const redirectLink = typeof item === 'string' ? null : item.link;

                        const content = (
                            <div 
                                className="h-20 w-24 md:h-44 md:w-80 flex items-center justify-center transition-all duration-700 bg-white p-2 rounded-none shadow-[0_10px_50px_-20px_rgba(0,0,0,0.1)] hover:shadow-[0_20px_70px_-15px_rgba(0,0,0,0.2)] transform hover:-translate-y-3 hover:scale-110 cursor-grab active:cursor-grabbing flex-shrink-0 border border-slate-100"
                            >
                                <img 
                                    src={getFullUrl(logoUrl)} 
                                    alt="Partner Logo" 
                                    className="max-h-[90%] max-w-[90%] object-contain pointer-events-none" 
                                />
                            </div>
                        );

                        return redirectLink ? (
                            <a 
                                key={idx} 
                                href={redirectLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="block flex-shrink-0 outline-none focus:ring-2 focus:ring-[#cc0000] focus:ring-offset-4"
                            >
                                {content}
                            </a>
                        ) : (
                            <React.Fragment key={idx}>{content}</React.Fragment>
                        );
                    })}
                </div>

                <div className="absolute right-0 top-0 bottom-0 w-10 md:w-80 bg-gradient-to-l from-white via-white/80 to-transparent z-10 pointer-events-none" />

                {/* Mobile Navigation Buttons */}
                <div className="md:hidden absolute left-2 top-1/2 -translate-y-1/2 z-20 flex gap-2 w-full justify-between px-2 pointer-events-none">
                    <button 
                        onClick={() => scroll(-200)}
                        className="w-8 h-8 rounded-full bg-white/90 shadow-md border border-slate-200 flex items-center justify-center text-[#cc0000] pointer-events-auto active:scale-95 transition-transform"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button 
                        onClick={() => scroll(200)}
                        className="w-8 h-8 rounded-full bg-white/90 shadow-md border border-slate-200 flex items-center justify-center text-[#cc0000] pointer-events-auto active:scale-95 transition-transform"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>
        </section>
    );
};

export default LogoMarquee;
